import React, { useEffect, useRef, useState } from 'react';
import { RepresentationType, ThumbnailType } from '../../../../bom/constants';

import AlertWrapper from '../../../common/AlertWrapper';
import FeedbackWrapper from '../../../common/FeedbackWrapper';
import Form from 'react-bootstrap/Form';
import GraphicOptionListbox from '../GraphicOptionListbox';
import PropTypes from 'prop-types';
import StepNumber from '../StepNumber';
import TooltipWrapper from '../../../common/TooltipWrapper';
import { getSpecialSpellingWords } from '../../../../main/selectors';
import styles from './GraphicOptionGroup.scss';
import { useSelector } from 'react-redux';

const GraphicOptionGroup = ({
  feedback,
  multiSelect = false,
  multiSelectable = false,
  options,
  previewable = false,
  question,
  showMultiSelectToggle = false,
  showToggleWarning = false,
  onCloseToggleWarning,
  onPreviewOption,
  onSelectOption,
  onToggleMultiSelect,
}) => {
  const specialWords = useSelector(getSpecialSpellingWords);
  const { color } = specialWords;
  const lowerCaseColor = color.toLowerCase();

  let { code, questionId, questionText, representationType, thumbnailType } = question;

  const listboxId = `question-${code}`;
  const labelId = `${listboxId}-label`;
  const descriptionId = `${listboxId}-description`;

  questionText = (questionText || '').trim();
  if (!questionText.endsWith(':')) questionText += ':';

  const parentChildRepresentation = representationType === RepresentationType.PARENT_CHILD;
  const showAlert = !!feedback[code] && parentChildRepresentation;
  const showFeedback = !!feedback[code] && !parentChildRepresentation;

  const listboxOptions = options.map((option) => ({ ...option, questionId }));
  const selectedOptions = options.filter((option) => option.isSelected);
  const hasNoColorReferences = options.every((option) => !option.colorReference);
  const needToUseTexture = options.some((option) => option.useTexture);

  const graphicType =
    thumbnailType === ThumbnailType.LOCAL_IMAGE && hasNoColorReferences
      ? ThumbnailType.LOCAL_IMAGE
      : thumbnailType === ThumbnailType.SCENE7_IMAGE && (hasNoColorReferences || needToUseTexture)
      ? ThumbnailType.SCENE7_IMAGE
      : ThumbnailType.COLOR_SWATCH;

  const toggleRef = useRef(null);

  const [toggleFocused, setToggleFocused] = useState(false);

  useEffect(() => {
    if (!!toggleRef && !!toggleRef.current && toggleFocused) {
      toggleRef.current.focus();
    }
  }, [multiSelect, toggleFocused]);

  const handleToggleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.click();
    }
  };

  const handleSelectOption = (option) => {
    if (!!onSelectOption && typeof onSelectOption === 'function') {
      onSelectOption(multiSelect, option);
    }
  };

  return (
    <Form.Group className={`${styles['graphic-option-group']} mb-3`}>
      {multiSelectable && showMultiSelectToggle ? (
        <>
          <AlertWrapper dismissible show={showToggleWarning} variant="warning" onClose={onCloseToggleWarning}>
            {`Please turn on the toggle to select multiple ${lowerCaseColor}s`}
          </AlertWrapper>
          <div className={styles['group-header']}>
            <Form.Label id={labelId}>
              <StepNumber step={code} />
              {questionText}
            </Form.Label>
            <div className={styles['toggle-holder']}>
              {multiSelect ? (
                <label className={styles['toggle-label']}>
                  {`Select Multiple ${color}s`}
                  <Form.Check
                    checked={multiSelect}
                    ref={toggleRef}
                    role="switch"
                    type="switch"
                    onBlur={() => setToggleFocused(false)}
                    onChange={onToggleMultiSelect}
                    onFocus={() => setToggleFocused(true)}
                    onKeyDown={handleToggleKeyDown}
                  />
                </label>
              ) : (
                <TooltipWrapper
                  id="multi-select-switch-tooltip"
                  message={`Order your design on multiple ${lowerCaseColor}s`}
                  trigger={['focus', 'hover']}>
                  {({ ref, ...triggerHandler }) => (
                    <label className={styles['toggle-label']} {...triggerHandler}>
                      {`Select Multiple ${color}s`}
                      <Form.Check
                        checked={multiSelect}
                        ref={(node) => {
                          ref(node);
                          toggleRef.current = node;
                        }}
                        role="switch"
                        type="switch"
                        onBlur={() => setToggleFocused(false)}
                        onChange={onToggleMultiSelect}
                        onFocus={() => setToggleFocused(true)}
                        onKeyDown={handleToggleKeyDown}
                      />
                    </label>
                  )}
                </TooltipWrapper>
              )}
              <span aria-hidden="true">{multiSelect ? 'On' : 'Off'}</span>
            </div>
          </div>
        </>
      ) : (
        <Form.Label id={labelId}>
          <StepNumber step={code} />
          {questionText}
        </Form.Label>
      )}
      {showAlert && <AlertWrapper variant="danger">{feedback[code]}</AlertWrapper>}
      <GraphicOptionListbox
        aria-describedby={selectedOptions.length > 0 ? descriptionId : ''}
        aria-labelledby={labelId}
        id={listboxId}
        graphicType={graphicType}
        isInvalid={!!feedback[code]}
        multiSelectable={multiSelectable && multiSelect}
        options={listboxOptions}
        previewable={previewable}
        onPreviewOption={onPreviewOption}
        onSelectOption={handleSelectOption}
      />
      {selectedOptions.length > 0 && (
        <Form.Text id={descriptionId} muted>
          {`Selected ${graphicType === ThumbnailType.COLOR_SWATCH ? color : 'Option'}(s): ${selectedOptions
            .map((option) => option.description)
            .join(', ')}`}
        </Form.Text>
      )}
      {showFeedback && <FeedbackWrapper>{feedback[code]}</FeedbackWrapper>}
    </Form.Group>
  );
};

GraphicOptionGroup.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  multiSelect: PropTypes.bool,
  multiSelectable: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      codeValue: PropTypes.string.isRequired,
      colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
      colorReference: PropTypes.string.isRequired,
      componentId: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      estimatedShipDate: PropTypes.string.isRequired,
      futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
      helpLink: PropTypes.shape({
        helpText: PropTypes.string.isRequired,
        linkText: PropTypes.string.isRequired,
      }),
      inventoryMessage: PropTypes.string.isRequired,
      isFutureShipDateSettable: PropTypes.bool.isRequired,
      isOutOfStock: PropTypes.bool.isRequired,
      isSelected: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      itemId: PropTypes.string.isRequired,
      optionText: PropTypes.string.isRequired,
      outOfStockMessage: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      sequence: PropTypes.number.isRequired,
      specialBomCharge: PropTypes.string.isRequired,
      useTexture: PropTypes.bool.isRequired,
      yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
    })
  ).isRequired,
  previewable: PropTypes.bool,
  question: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  showMultiSelectToggle: PropTypes.bool,
  showToggleWarning: PropTypes.bool,
  onCloseToggleWarning: PropTypes.func,
  onPreviewOption: PropTypes.func,
  onSelectOption: PropTypes.func,
  onToggleMultiSelect: PropTypes.func,
};

export default GraphicOptionGroup;
